/**
 * How one policy (packages/types/src/policies.ts) is shown and edited in Settings > Your agents: its title, a form for
 * its value, and a card with Save and Reset. The person's own section and the firm's defaults both use it.
 */
import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, Chip, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { POLICY_SCHEMAS, type PolicyKey, type PolicyValues } from '@diggens/types';

export const POLICY_TEXT: { [K in PolicyKey]: { title: string; description: string } } = {
  reply_style: { title: 'Replies', description: 'How long the Chief of Staff and CRM agent make their replies, and how they lay them out.' },
  writing_style: { title: 'Emails and notes in your name', description: 'How the BDR drafts outreach emails and HubSpot notes.' },
  about_me: { title: 'About you', description: 'Your role and what you focus on, so agents frame their work around it.' },
  standing_instructions: { title: 'Standing instructions', description: 'Rules every agent follows when it works for you, up to 10. They shape the work; they never approve anything.' },
  working_hours: { title: 'Working hours', description: 'Your morning digest arrives when your day starts, and a task given no due date falls on your next workday.' },
  research_depth: { title: 'Research depth', description: 'How much the Research agent looks up, and pays for, each time.' },
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Every IANA zone the browser knows; ES2020's lib does not declare supportedValuesOf, which every current browser has. */
const TIME_ZONES: readonly string[] = (Intl as unknown as { supportedValuesOf(key: 'timeZone'): string[] }).supportedValuesOf('timeZone');
const TONES = ['warm', 'neutral', 'formal', 'casual', 'direct'] as const;

/** A text field's value as the policy keeps it: empty means not set. */
function optional(text: string): string | undefined {
  return text.trim() === '' ? undefined : text;
}

function hour(value: number): string {
  return `${String(value).padStart(2, '0')}:00`;
}

/** One line saying what a value amounts to, such as a firm default shown above the person's own. */
export function describePolicy<K extends PolicyKey>(key: K, value: PolicyValues[K]): string {
  const v = value as PolicyValues[PolicyKey];
  switch (key) {
    case 'reply_style': {
      const { length, format } = v as PolicyValues['reply_style'];
      return [length, format === 'bullets' ? 'bullet points' : format === 'prose' ? 'paragraphs' : undefined].filter(Boolean).join(', ') || 'Agent default';
    }
    case 'writing_style': {
      const { tone, maxWords, greeting, signOff } = v as PolicyValues['writing_style'];
      return (
        [tone, maxWords ? `under ${maxWords} words` : undefined, greeting ? `opens "${greeting}"` : undefined, signOff ? `signs off "${signOff}"` : undefined].filter(Boolean).join(', ') ||
        'Agent default'
      );
    }
    case 'about_me': {
      const { role, focus } = v as PolicyValues['about_me'];
      return [role, focus].filter(Boolean).join(' · ') || 'Nothing set';
    }
    case 'standing_instructions': {
      const instructions = v as PolicyValues['standing_instructions'];
      return instructions.length === 0 ? 'None' : instructions.map((instruction) => `"${instruction}"`).join('; ');
    }
    case 'working_hours': {
      const { workdays, startHour, timeZone } = v as PolicyValues['working_hours'];
      return `${[...workdays]
        .sort((a, b) => a - b)
        .map((day) => DAYS[day - 1])
        .join(', ')} from ${hour(startHour)}${timeZone ? ` in ${timeZone}` : ''}`;
    }
    default:
      return String(v);
  }
}

interface FieldsProps<K extends PolicyKey> {
  value: PolicyValues[K];
  onChange: (value: PolicyValues[K]) => void;
  disabled: boolean;
}

function ReplyStyleFields({ value, onChange, disabled }: FieldsProps<'reply_style'>) {
  return (
    <Stack direction="row" spacing={2}>
      <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
        <InputLabel id="reply-length">Length</InputLabel>
        <Select
          labelId="reply-length"
          label="Length"
          value={value.length ?? ''}
          onChange={(event) => onChange({ ...value, length: (event.target.value || undefined) as PolicyValues['reply_style']['length'] })}
        >
          <MenuItem value="">Agent default</MenuItem>
          <MenuItem value="brief">Brief</MenuItem>
          <MenuItem value="detailed">Detailed</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
        <InputLabel id="reply-format">Format</InputLabel>
        <Select
          labelId="reply-format"
          label="Format"
          value={value.format ?? ''}
          onChange={(event) => onChange({ ...value, format: (event.target.value || undefined) as PolicyValues['reply_style']['format'] })}
        >
          <MenuItem value="">Agent default</MenuItem>
          <MenuItem value="bullets">Bullet points</MenuItem>
          <MenuItem value="prose">Paragraphs</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
}

function WritingStyleFields({ value, onChange, disabled }: FieldsProps<'writing_style'>) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
          <InputLabel id="writing-tone">Tone</InputLabel>
          <Select
            labelId="writing-tone"
            label="Tone"
            value={value.tone ?? ''}
            onChange={(event) => onChange({ ...value, tone: (event.target.value || undefined) as PolicyValues['writing_style']['tone'] })}
          >
            <MenuItem value="">Agent default</MenuItem>
            {TONES.map((tone) => (
              <MenuItem key={tone} value={tone}>
                {tone[0]!.toUpperCase() + tone.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="number"
          label="Most words"
          helperText="40 to 400"
          disabled={disabled}
          value={value.maxWords ?? ''}
          onChange={(event) => onChange({ ...value, maxWords: event.target.value === '' ? undefined : Number(event.target.value) })}
        />
      </Stack>
      <TextField
        size="small"
        label="Greeting"
        placeholder="Hi [first name],"
        disabled={disabled}
        value={value.greeting ?? ''}
        onChange={(event) => onChange({ ...value, greeting: optional(event.target.value) })}
      />
      <TextField
        size="small"
        label="Sign-off"
        placeholder="Best, Nicole"
        multiline
        disabled={disabled}
        value={value.signOff ?? ''}
        onChange={(event) => onChange({ ...value, signOff: optional(event.target.value) })}
      />
    </Stack>
  );
}

function AboutMeFields({ value, onChange, disabled }: FieldsProps<'about_me'>) {
  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        label="Your role"
        placeholder="Head of business development"
        disabled={disabled}
        value={value.role ?? ''}
        onChange={(event) => onChange({ ...value, role: optional(event.target.value) })}
      />
      <TextField
        size="small"
        label="What you focus on"
        placeholder="US healthcare buyouts, $50M to $500M EV"
        multiline
        disabled={disabled}
        value={value.focus ?? ''}
        onChange={(event) => onChange({ ...value, focus: optional(event.target.value) })}
      />
      <TextField
        size="small"
        label="Anything else agents should know"
        multiline
        minRows={2}
        disabled={disabled}
        value={value.note ?? ''}
        onChange={(event) => onChange({ ...value, note: optional(event.target.value) })}
      />
    </Stack>
  );
}

function StandingInstructionsFields({ value, onChange, disabled }: FieldsProps<'standing_instructions'>) {
  return (
    <Stack spacing={1}>
      {value.map((instruction, index) => (
        <Stack key={index} direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            fullWidth
            label={`Instruction ${index + 1}`}
            disabled={disabled}
            value={instruction}
            onChange={(event) => onChange(value.map((existing, at) => (at === index ? event.target.value : existing)))}
          />
          <Button size="small" disabled={disabled} onClick={() => onChange(value.filter((_, at) => at !== index))}>
            Remove
          </Button>
        </Stack>
      ))}
      {value.length < 10 && (
        <Box>
          <Button size="small" disabled={disabled} onClick={() => onChange([...value, ''])}>
            Add an instruction
          </Button>
        </Box>
      )}
    </Stack>
  );
}

function WorkingHoursFields({ value, onChange, disabled }: FieldsProps<'working_hours'>) {
  const toggle = (day: number) => onChange({ ...value, workdays: value.workdays.includes(day) ? value.workdays.filter((existing) => existing !== day) : [...value.workdays, day] });
  return (
    <Stack spacing={2}>
      <FormGroup row>
        {DAYS.map((label, index) => (
          <FormControlLabel key={label} label={label} disabled={disabled} control={<Checkbox checked={value.workdays.includes(index + 1)} onChange={() => toggle(index + 1)} />} />
        ))}
      </FormGroup>
      <Stack direction="row" spacing={2}>
        <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
          <InputLabel id="start-hour">Day starts at</InputLabel>
          <Select labelId="start-hour" label="Day starts at" value={value.startHour} onChange={(event) => onChange({ ...value, startHour: Number(event.target.value) })}>
            {Array.from({ length: 24 }, (_, at) => (
              <MenuItem key={at} value={at}>
                {hour(at)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 280 }} disabled={disabled}>
          <InputLabel id="time-zone">Time zone</InputLabel>
          <Select
            labelId="time-zone"
            label="Time zone"
            value={value.timeZone ?? ''}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            onChange={(event) => onChange({ ...value, timeZone: event.target.value || undefined })}
          >
            <MenuItem value="">The one HubSpot set</MenuItem>
            {TIME_ZONES.map((zone) => (
              <MenuItem key={zone} value={zone}>
                {zone}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
}

function ResearchDepthFields({ value, onChange, disabled }: FieldsProps<'research_depth'>) {
  return (
    <FormControl size="small" sx={{ maxWidth: 240 }} disabled={disabled}>
      <InputLabel id="research-depth">Depth</InputLabel>
      <Select labelId="research-depth" label="Depth" value={value} onChange={(event) => onChange(event.target.value as PolicyValues['research_depth'])}>
        <MenuItem value="quick">Quick: overview and key people</MenuItem>
        <MenuItem value="standard">Standard</MenuItem>
        <MenuItem value="deep">Deep: people, metrics and news</MenuItem>
      </Select>
    </FormControl>
  );
}

const FIELDS: { [K in PolicyKey]: (props: FieldsProps<K>) => ReactNode } = {
  reply_style: ReplyStyleFields,
  writing_style: WritingStyleFields,
  about_me: AboutMeFields,
  standing_instructions: StandingInstructionsFields,
  working_hours: WorkingHoursFields,
  research_depth: ResearchDepthFields,
};

/** Blank instructions are left out rather than saved, since each one must say something. */
function cleaned<K extends PolicyKey>(key: K, value: PolicyValues[K]): PolicyValues[K] {
  return key === 'standing_instructions' ? ((value as PolicyValues['standing_instructions']).filter((instruction) => instruction.trim() !== '') as PolicyValues[K]) : value;
}

interface PolicyCardProps<K extends PolicyKey> {
  policyKey: K;
  /** What the form starts from. Fresh data replaces the draft only while it has no edits of its own. */
  initial: PolicyValues[K];
  /** What the server holds that this form edits, such as the person's own value; a save or reset says which it started from. */
  revision: unknown;
  /** Where what applies comes from, such as "Yours" or "Your firm's default". */
  source: string;
  /** A line under the description, such as the firm's default. */
  note?: string;
  disabled: boolean;
  saving: boolean;
  error: string | null;
  onSave: (value: PolicyValues[K], startedFrom: unknown) => void;
  /** Shown when there is something to reset. */
  onReset?: (startedFrom: unknown) => void;
  resetLabel?: string;
  /** More controls beside Save, such as the firm's lock; as a function, told whether the form has unsaved edits. */
  actions?: ReactNode | ((edited: boolean) => ReactNode);
  /** Save even when the form still shows what it started from, so a firm can adopt (and then lock) the preset. */
  saveUnchanged?: boolean;
}

/** `value` with every object's keys sorted, so two values compare alike whatever order their keys came in. */
function sortedKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortedKeys);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortedKeys((value as Record<string, unknown>)[key])])
    );
  }
  return value;
}

/** Whether two values are the same, whatever the order of their keys, such as a draft and what the server returned. */
const same = (a: unknown, b: unknown) => JSON.stringify(sortedKeys(a)) === JSON.stringify(sortedKeys(b));

/** Whether two values of a policy are the same once the server's schema has read them: trimmed, as it stores them. */
function samePolicy(key: PolicyKey, a: unknown, b: unknown): boolean {
  const read = (value: unknown) => {
    const parsed = POLICY_SCHEMAS[key].safeParse(value);
    return parsed.success ? parsed.data : value;
  };
  return same(read(a), read(b));
}

export function PolicyCard<K extends PolicyKey>({
  policyKey,
  initial,
  revision,
  source,
  note,
  disabled,
  saving,
  error,
  onSave,
  onReset,
  resetLabel = 'Reset',
  actions,
  saveUnchanged = false,
}: PolicyCardProps<K>) {
  const [draft, setDraft] = useState(initial);
  /** What the draft started from, and what the server held then. */
  const [base, setBase] = useState({ initial, revision });
  const edited = !samePolicy(policyKey, cleaned(policyKey, draft), base.initial);
  const fresh = !same(revision, base.revision) || !samePolicy(policyKey, initial, base.initial);
  // Fresh data, from another tab, chat or Slack, replaces a draft with no edits of its own, or one that already says the
  // same, as after a save; an edited draft is kept, and the card says it changed elsewhere
  if (fresh && (!edited || samePolicy(policyKey, cleaned(policyKey, draft), initial))) {
    setBase({ initial, revision });
    setDraft(initial);
  }
  const outdated = fresh && edited && !samePolicy(policyKey, cleaned(policyKey, draft), initial);
  const reload = () => {
    setBase({ initial, revision });
    setDraft(initial);
    setInvalid(null);
  };
  /** What is wrong with the draft, found before it is sent, so an invalid value never reaches the API. */
  const [invalid, setInvalid] = useState<string | null>(null);
  // FIELDS pairs each key with its own editor, but indexing it with a generic key widens to every editor at once
  const Fields = FIELDS[policyKey] as (props: FieldsProps<K>) => ReactNode;
  const { title, description } = POLICY_TEXT[policyKey];
  const changed = saveUnchanged || edited;
  const save = () => {
    const parsed = POLICY_SCHEMAS[policyKey].safeParse(cleaned(policyKey, draft));
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setInvalid(issue ? `${issue.path.length > 0 ? `${issue.path.join(' ')}: ` : ''}${issue.message}` : 'This value is not valid.');
      return;
    }
    setInvalid(null);
    onSave(parsed.data, base.revision);
  };
  const shown = invalid ?? error;

  return (
    <Card component="section" sx={{ maxWidth: 800 }} aria-label={title}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <Chip size="small" label={source} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: note ? 1 : 2 }}>
          {description}
        </Typography>
        {note && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {note}
          </Typography>
        )}
        {shown && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {shown}
          </Alert>
        )}
        {outdated && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={reload}>
                Reload
              </Button>
            }
          >
            This changed elsewhere; reload to see the latest.
          </Alert>
        )}
        <Fields
          value={draft}
          onChange={(next) => {
            setDraft(next);
            // A change answers what was wrong, and must not hide an error from the server that comes after it
            setInvalid(null);
          }}
          disabled={disabled || saving}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
          <Button variant="contained" disabled={disabled || saving || !changed} onClick={save}>
            Save
          </Button>
          {onReset && (
            <Button
              disabled={disabled || saving}
              onClick={() => {
                // Resetting drops the draft, so its problem goes too, and a failed reset shows in its place
                setInvalid(null);
                onReset(base.revision);
              }}
            >
              {resetLabel}
            </Button>
          )}
          {typeof actions === 'function' ? actions(edited) : actions}
        </Stack>
      </CardContent>
    </Card>
  );
}
